import { useState, useEffect } from 'react';
import api from '../services/api';
import type {
  AmazonAffiliateSettings,
  AmazonAffiliateSettingsUpdate,
} from '../types';

// Per-user Amazon Associates settings. Phase 1 uses the affiliate tag: on save
// the backend provisions/updates an ad-server Account for this user so product
// links served on their pages are credited to their tag. The PA-API key fields
// are stored (secret encrypted) for a later "bring your own credentials" phase;
// nothing uses them yet, hence the muted "coming soon" note.
export default function AmazonAffiliateSettingsPage() {
  const [settings, setSettings] = useState<AmazonAffiliateSettings | null>(null);
  const [form, setForm] = useState<AmazonAffiliateSettingsUpdate>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api
      .getAmazonAffiliateSettings()
      .then((data) => {
        setSettings(data);
        setForm({
          is_enabled: data.is_enabled,
          associate_tag: data.associate_tag ?? '',
          access_key_id: data.access_key_id ?? '',
          secret_access_key: '', // empty = leave stored secret unchanged
        });
      })
      .catch((err) =>
        setError(err instanceof Error ? err.message : 'Failed to load settings')
      )
      .finally(() => setLoading(false));
  }, []);

  const setField = (
    key: keyof AmazonAffiliateSettingsUpdate,
    value: string | boolean
  ) => setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      const payload: AmazonAffiliateSettingsUpdate = {
        is_enabled: form.is_enabled,
        associate_tag: (form.associate_tag ?? '').trim() || null,
        access_key_id: (form.access_key_id ?? '').trim() || null,
      };
      // Only send the secret when the user actually typed one — empty means
      // "leave unchanged" (the backend enforces this too).
      if (form.secret_access_key && form.secret_access_key.trim()) {
        payload.secret_access_key = form.secret_access_key.trim();
      }
      const updated = await api.updateAmazonAffiliateSettings(payload);
      setSettings(updated);
      setForm((f) => ({ ...f, secret_access_key: '' }));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return <div className="text-center py-8 text-gray-500 dark:text-gray-400">Loading...</div>;

  const synced = !!settings?.adserver_account_id;

  return (
    <div className="max-w-2xl">
      <div className="flex justify-between items-center mb-1">
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Amazon Affiliate</h2>
        {settings?.updated_at && (
          <span className="text-xs text-gray-400 dark:text-gray-500">
            Last updated {new Date(settings.updated_at).toLocaleString()}
          </span>
        )}
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Credit product links the page builder places on your pages to your Amazon
        Associates account.
      </p>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 border rounded-lg divide-y divide-gray-100 dark:divide-gray-700">
        {/* Enable */}
        <div className="px-4 py-4 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Enabled</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              When off, product searches fall back to the server default account.
            </p>
          </div>
          <input
            type="checkbox"
            checked={!!form.is_enabled}
            onChange={(e) => setField('is_enabled', e.target.checked)}
            className="h-4 w-4"
          />
        </div>

        {/* Associate tag */}
        <div className="px-4 py-4">
          <label className="block text-sm font-medium text-gray-900 dark:text-gray-100">
            Associate tag
          </label>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-2">
            Your Amazon tracking ID, e.g. <code>yourstore-20</code>. Clicks are
            credited here.
          </p>
          <input
            type="text"
            value={form.associate_tag ?? ''}
            onChange={(e) => setField('associate_tag', e.target.value)}
            placeholder="yourstore-20"
            className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm"
          />
          <div className="mt-2 text-xs">
            {synced ? (
              <span className="text-green-600 dark:text-green-400">
                ✓ Linked to ad-server account {settings?.adserver_account_id}
              </span>
            ) : (
              <span className="text-gray-400 dark:text-gray-500">
                Not yet linked to the ad-server - save to provision.
              </span>
            )}
          </div>
        </div>

        {/* PA-API creds (phase 2 — stored but not yet used) */}
        <div className="px-4 py-4 opacity-90">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Product Advertising API credentials
            </p>
            <span className="text-[10px] uppercase tracking-wide bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-1.5 py-0.5 rounded">
              Coming soon
            </span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            Optional. When per-account API access ships, your searches will run
            under your own PA-API quota instead of the shared one. Stored
            securely (secret encrypted); nothing uses these yet.
          </p>
          <input
            type="text"
            value={form.access_key_id ?? ''}
            onChange={(e) => setField('access_key_id', e.target.value)}
            placeholder="Access key ID"
            className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm mb-2"
          />
          <input
            type="password"
            value={form.secret_access_key ?? ''}
            onChange={(e) => setField('secret_access_key', e.target.value)}
            placeholder={
              settings?.has_secret
                ? '•••••••• (leave blank to keep)'
                : 'Secret access key'
            }
            className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm"
          />
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        {saved && <span className="text-sm text-green-600 dark:text-green-400">Saved</span>}
      </div>
    </div>
  );
}
